import React, { useState, useRef, useLayoutEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import styled from '@emotion/native';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import SectionLayout from '../SectionLayout';
import Dropdown from '@/shared/components/Dropdown';
import { US_TIMEZONES } from '@/shared/constants/availability';
import { SectionTitle } from '../styledComponents';
import useAvailabilityStore from '@/stores/useAvailabilityStore';
import { StepComponentProps } from '@/shared/types/availability';
import useDirty from '@/shared/hooks/useDirty';
import { getDefaultTimezone } from '../utils';

const TimezoneStep = ({
  isAllCompleted,
  onSaveAvailability,
}: StepComponentProps) => {
  const { isDesktop } = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const initializationRef = useRef(false);

  const { timezone, setTimezone } = useAvailabilityStore(
    useShallow((state) => ({
      timezone: state.timezone,
      setTimezone: state.setTimezone,
    })),
  );

  const displayTimezone = timezone || getDefaultTimezone();

  // Initialize timezone after render but before paint
  useLayoutEffect(() => {
    if (!timezone && !initializationRef.current) {
      const defaultTz = getDefaultTimezone();
      setTimezone(defaultTz);
      initializationRef.current = true;
    }
  }, [timezone, setTimezone]);

  const isDirty = useDirty(timezone);
  const isValid = Boolean(timezone || initializationRef.current);

  const isButtonDisabled =
    loading || (!isDirty && !initializationRef.current) || !isValid;

  const saveAvailability = async () => {
    if (!onSaveAvailability) return;

    // If timezone wasn't explicitly set but we detected one, make sure it's in the store
    if (!timezone && initializationRef.current) {
      setTimezone(getDefaultTimezone());
    }

    setLoading(true);
    try {
      await onSaveAvailability();
    } finally {
      setLoading(false);
    }
  };

  // Find the selected timezone name for display
  const selectedTimezone =
    US_TIMEZONES.find((tz) => tz.value === displayTimezone)?.label ||
    'Select a timezone';

  const dropdownWidth = {
    width: isDesktop ? 438 : 339,
  };

  return (
    <SectionLayout
      heading="Time travel? Not quite."
      description="Tell us where you are in the world so we can make sure bookings line up just right. Jet lag not included."
      buttonText={isAllCompleted ? 'Save Changes' : 'Continue'}
      onButtonPress={saveAvailability}
      isButtonDisabled={isButtonDisabled}
      isButtonLoading={loading}
      descriptionStyle={{ maxWidth: isDesktop ? 400 : 300 }}
      titleStyle={{ maxWidth: isDesktop ? '100%' : 214 }}
    >
      <TimezoneSection>
        <SectionTitle>Timezone*</SectionTitle>
        <DropdownContainer>
          <Dropdown
            options={US_TIMEZONES}
            selectedOption={selectedTimezone || 'Select a timezone'}
            onSelect={(option) => setTimezone(String(option.value))}
            isVisible={isDropdownVisible}
            setIsVisible={setIsDropdownVisible}
            dropdownTextStyle={dropdownTextStyle}
            customMenuContentStyle={dropdownWidth}
            customDropdownButtonStyle={{
              ...dropdownWidth,
              marginBottom: 14,
            }}
          />
        </DropdownContainer>
      </TimezoneSection>
    </SectionLayout>
  );
};

export default TimezoneStep;

const dropdownTextStyle = {
  fontSize: 14,
};

const TimezoneSection = styled.View({
  width: '100%',
  marginTop: 16,
  maxWidth: 438,
});

const DropdownContainer = styled.View({
  width: '100%',
  marginTop: 8,
});
